import React, { useRef } from 'react';
import styled from 'styled-components';
import toolChangelog from './tool.changes.md';

import { HandleType, NodeToolbar, Position } from 'reactflow';
import { useNodeOutline } from '@common/hooks/useNodeOutline';
import { useSelector } from 'react-redux';
import { selectCommandByNodeId, State } from '@store/index';
import { useTheme } from '../../../../../styles';
import { Tooltip } from '@common/Tooltip';
import { Badge, Chip, Stack } from '@mui/material';
import { useNodeError } from '@common/hooks/useNodeError';
import { NodeAnchors, NodeBodyContainer, NodeContainer, NodeErrorToolbar, NodeHeader } from '@common/Node';

import {
  FieldValidation,
  NodeConfig,
  NodeField,
  NodeFieldType,
  NodeInputType,
  NodeOutput,
  NodeType,
  ToolNodeState,
} from '../../';

export enum ToolExtension {
  BucketSearch = 'bucket_search',
  APISearch = 'api_search',
  PricingCalculator = 'pricing_calculator',
  CustomOutput = 'custom_output',
  HTTPRequest = 'http_request',
}

const Extensions = styled.div`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.35rem;
  flex-wrap: wrap;
  color: ${({ theme }) => theme.text.subtext};

  padding: 0.75rem 1rem;
  border-radius: 7px 7px 0 0;
  font-family: 'Inter';

  border-bottom: 1px solid ${({ theme }) => theme.line.node};

  & .MuiBadge-badge {
    color: white;
    height: 12px;
    min-width: 12px !important;
    max-width: 12px !important;
    font-size: 8px;
    padding: 0;
    left: -10 !important;
    margin-right: 0.25rem;
    margin-top: 0.15rem;
  }
`;

export const config: NodeConfig = {
  version: '1.1',
  title: 'AI Tool',
  identifier: 'Tool',
  background: '#3434EF',
  command_id: 'llm.tool',
  icon: 'tool',
  changelog: toolChangelog,
  anchors: [
    {
      label: 'Tool',
      id: 'llm_function.output',
      type: 'source' as HandleType,
      variant: 'plugin',
    },
  ],
};

export const fields: NodeField[] = [
  {
    id: 'name',
    type: NodeFieldType.Input,
    default_type: NodeInputType.Text,
    label: 'Tool Name',
    display_name: true,
    description:
      'Use this field to give your tool a definitive name. Keep it short and simple as this counts towards the token count.',
    placeholder: 'contact_support',
    required: true,
    validation: FieldValidation.variable,
    tab: '1',
  },
  {
    id: 'description',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Text,
    label: 'Tool Description',
    description: 'Define a description for this tool that the LLM will use to determine when to call it.',
    placeholder: 'Describe the tool',
    required: true,
    tab: '1',
  },
  {
    id: 'system',
    type: NodeFieldType.InputExpression,
    default_type: NodeInputType.Text,
    label: 'System Message',
    description: 'Define instructions for the LLM to use when writing an answer after using this function.',
    placeholder: 'Write a system message',
    tab: '1',
  },
  {
    id: 'args',
    type: NodeFieldType.ToolArgs,
    label: 'Arguments',
    description:
      'Add arguments to be generated by the LLM when this tool is called. These arguments can be used as dynamic variables in the "Output" tab.\n\nThese arguments will only be accessible from the "Output" tab for this tool.\n\n- The name of the argument is what it will be defined as to the LLM.\n -The type of the argument is the type of the value that the LLM is generating, in most cases will be a string.\n- The description of the argument is what the LLM will use to know how to generate it.',
    tab: '2',
  },
  {
    id: 'custom_output',
    type: ToolExtension.CustomOutput,
    label: 'Tool Output',
    description:
      'Manually write the output of this tool. Enabling this output will prevent you from using any other outputs. You can use dynamic variables to create dynamic outputs.',
    placeholder: '',
    tab: '3',
  },
  {
    id: 'bucket_search',
    type: ToolExtension.BucketSearch,
    label: 'Index Search',
    description:
      'Search for files from one or more buckets. Use weights to give a particular bucket more priority over another when searching.',
    placeholder: '',
    tab: '3',
  },
  {
    id: 'api_search',
    type: ToolExtension.APISearch,
    label: 'Telnyx API Search',
    description:
      'Search the internally maintained Telnyx API specification bucket and determine which attributes to show to the LLM after searching.',
    placeholder: '',
    tab: '3',
  },
  {
    id: 'pricing_calculator',
    type: ToolExtension.PricingCalculator,
    label: 'Pricing Calculator',
    description: 'Calculate the cost of using a Telnyx product.',
    placeholder: '',
    tab: '3',
  },
  {
    id: 'http_request',
    type: ToolExtension.HTTPRequest,
    label: 'HTTP Request',
    description: 'Send a HTTP request and use the response as the tool output.',
    placeholder: '',
    tab: '3',
  },
  {
    id: 'after',
    type: NodeFieldType.After,
    label: 'After Tool',
    description:
      'Set the behavior of the LLM after this tool is called. You can choose to show feedback, show a help action, or call another tool.',
    placeholder: '',
    tab: '4',
  },
];

export const state: ToolNodeState = {
  name: '',
  description: {
    type: NodeInputType.Text,
    value: '',
  },
  system: {
    type: NodeInputType.Text,
    value: '',
  },
  args: [],
  after: {
    tool_choice: {
      type: NodeInputType.Option,
      value: 'auto',
    },
    show_feedback: {
      type: NodeInputType.Option,
      value: 'false',
    },
    show_help_action: {
      type: NodeInputType.Option,
      value: 'false',
    },
  },
  bucket_search: {
    enabled: false,
    search_descriptor: {
      type: NodeInputType.Expression,
      value: '',
    },
    bucket_search_type: {
      type: NodeInputType.Option,
      value: 'list_and_search',
    },
    backup_output: {
      type: NodeInputType.Text,
      value: '',
    },
    backup_tool_choice: {
      type: NodeInputType.Option,
      value: 'auto',
    },
    max_tokens: 2000,
    max_results: 3,
    indexes: [],
  },
  api_search: {
    enabled: false,
    search_descriptor: {
      type: NodeInputType.Expression,
      value: '',
    },
    bucket_search_type: {
      type: NodeInputType.Option,
      value: 'list_and_search',
    },
    max_tokens: 2000,
    max_results: 3,
    query_parameters: {
      type: NodeInputType.Option,
      value: 'false',
    },
    body_parameters: {
      type: NodeInputType.Option,
      value: 'false',
    },
    responses: {
      type: NodeInputType.Option,
      value: 'false',
    },
    webhooks: {
      type: NodeInputType.Option,
      value: 'false',
    },
    sdk: {
      type: NodeInputType.Option,
      value: 'none',
    },
    backup_output: {
      type: NodeInputType.Text,
      value: '',
    },
    backup_tool_choice: {
      type: NodeInputType.Option,
      value: 'auto',
    },
  },
  pricing_calculator: {
    enabled: false,
    product: {
      type: NodeInputType.Option,
      value: 'sms',
    },
    countryCode: {
      type: NodeInputType.Option,
      value: 'us',
    },
  },
  custom_output: {
    enabled: false,
    output: {
      type: NodeInputType.Expression,
      value: '',
    },
  },
  http_request: {
    enabled: false,
    method: {
      type: NodeInputType.Option,
      value: 'get',
    },
    url: {
      value: '',
      type: NodeInputType.Expression,
    },
    query_parameters: [],
    header_parameters: [],
    body_format: 'form',
    body_parameters: [],
    body_json: { type: NodeInputType.Expression, value: `{\n   "example": "value"\n}` },
    auth_enabled: false,
    auth_secret_name: '',
  },
};

export const output: NodeOutput = {
  default: null,
  dynamic: null,
};

export const Tool: React.FC<NodeType> = ({ id, selected, data }) => {
  const theme = useTheme();
  const nodeRef = useRef(null);
  const themeType = useSelector((state: State) => state.flow.theme);
  const command = useSelector((state: State) => selectCommandByNodeId(state, id));

  const { isNodeError, nodeError, nodeStatus } = useNodeError({ command });
  const { nodeOutline } = useNodeOutline({ command, selected });

  if (!command) return null;
  if (command.config.command_id !== 'llm.tool') return null;

  const { title, anchors, icon } = config;
  const background = themeType === 'dark' ? 'rgb(88, 88, 255)' : theme.telnyx.blue;

  const nodeState: ToolNodeState = command.state;
  const pricing_calculator = nodeState.pricing_calculator;
  const custom_output = nodeState.custom_output;
  const bucket_search = nodeState?.bucket_search;
  const api_search = nodeState.api_search;

  const displayFieldId = command.fields.find((field) => field.display_name)?.id;
  const displayTitle = displayFieldId ? (nodeState[displayFieldId as keyof ToolNodeState] as string) : null;

  const isToolbarVisible = Boolean(isNodeError);

  const extensions = [
    nodeState?.api_search?.enabled ? 'API Search' : null,
    nodeState?.bucket_search?.enabled ? 'Bucket Search' : null,
    nodeState?.pricing_calculator?.enabled ? 'Pricing Calculator' : null,
    nodeState?.custom_output?.enabled ? 'Custom Output' : null,
  ].filter(Boolean);

  const TTCustomOutput = [
    `Custom Output`,
    custom_output?.output.value.length > 1000
      ? '\nThe custom output is too long to display in the tooltip. Open the "Output" tab of this node to view the full output.'
      : `\n${custom_output?.output.value}`,
  ].join('\n');

  const TTBucketSearch = [
    `Bucket Search`,
    `\nMax Results: ${bucket_search?.max_results}`,
    `Max Tokens: ${bucket_search?.max_tokens}`,
    `Search Type: ${bucket_search?.bucket_search_type?.value}`,
    `Search Descriptor: ${bucket_search?.search_descriptor?.value}`,
    `\nIndexes: ${bucket_search?.indexes?.map((idx: any) => idx.index).join(', ')}`,
  ].join('\n');

  const TTPricingCalculator = [
    `Pricing Calculator`,
    `\nProduct: ${pricing_calculator?.product.value}`,
    `Country Code: ${pricing_calculator?.countryCode.value}`,
  ].join('\n');

  const TTAPISearch = [
    `Telnyx API Search`,
    `\nSearch Descriptor: ${api_search?.search_descriptor.value}`,
    `Search Type: ${api_search?.bucket_search_type?.value}`,
    `\nMax Results: ${api_search?.max_results}`,
    `Max Tokens: ${api_search?.max_tokens}`,
    `\nInclude Parameters: ${api_search?.query_parameters.value}`,
    `Include Request Body: ${api_search?.body_parameters.value}`,
    `Include Responses: ${api_search?.responses.value}`,
    `Include Webhooks: ${api_search?.webhooks.value}`,
    `Include SDK Samples: ${api_search?.sdk.value}`,
  ].join('\n');

  const getBadgeValue = (extension: string | null) => {
    if (extension === ToolExtension.BucketSearch)
      return nodeState?.bucket_search.indexes.filter((idx: any) => idx.index).length;

    return 0;
  };

  const getTooltip = (extension: string | null) => {
    if (extension === 'Pricing Calculator') return TTPricingCalculator;
    if (extension === 'Custom Output') return TTCustomOutput;
    if (extension === 'Bucket Search') return TTBucketSearch;
    if (extension === 'API Search') return TTAPISearch;
    if (extension === 'HTTP Request') return 'A HTTP request will be sent when this tool is called.';

    return 'No Tooltip Info';
  };

  return (
    <NodeContainer selected={selected} nodeOutline={nodeOutline} error={false} ref={nodeRef}>
      <NodeToolbar offset={18} isVisible={isToolbarVisible} position={Position.Top}>
        <Stack direction='row' spacing={1}>
          {isNodeError && <NodeErrorToolbar error={nodeError} />}
        </Stack>
      </NodeToolbar>
      <NodeHeader id={id} title={displayTitle || title} icon={icon} background={background} status={nodeStatus} />
      {nodeState.description.value && <NodeBodyContainer>{nodeState.description.value}</NodeBodyContainer>}
      {extensions.length > 0 && (
        <Extensions>
          {extensions.map((extension) => (
            <Tooltip sx={{ maxWidth: '500px' }} key={extension} title={getTooltip(extension)} placement='top' arrow>
              <Badge badgeContent={getBadgeValue(extension)} color='primary'>
                <Chip onClick={() => null} label={extension} variant='outlined' size='small' sx={{ fontSize: '9px' }} />
              </Badge>
            </Tooltip>
          ))}
        </Extensions>
      )}
      <NodeAnchors id={id} command={command} data={data} anchors={anchors} />
    </NodeContainer>
  );
};

export default { config, state, fields, output };
